import { useEffect, useRef, useState } from "react";
import { replace, useParams } from "react-router-dom"; // ✅ added
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import { FiCamera } from "react-icons/fi";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { ref, set, push, get, update } from "firebase/database"; // ✅ added get & update
import { database, storage } from "../constants/firebase";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import { convertToWebP, resizeImage } from "../utils/utils";

const NewCommunity = () => {
    const navigate = useMoveTo();
    const { id } = useParams(); // ✅ Get ID if in edit mode
    const isEditMode = Boolean(id);
    const { currentUser } = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [communityImage, setCommunityImage] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState("");
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
        name: "",
        description: "",
    });

    // ✅ Fetch community data when editing
    useEffect(() => {
        if (!isEditMode) return;
        const communityRef = ref(database, `Communities/${id}`);
        get(communityRef).then((snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setForm({
                    name: data.name || "",
                    description: data.description || "",
                });
                setExistingImageUrl(data.imageUrl || "");
            }
        });
    }, [id, isEditMode]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const resizedBlob = await resizeImage(file, 200); // 200px x 200px
            setCommunityImage(resizedBlob);
        } catch (err) {
            console.error("Image resize failed:", err);
        }
    };

    const handleSave = async () => {
        if (!currentUser) return;
        if (!form.name.trim()) {
            alert("Community name is required");
            return;
        }

        setIsSubmitting(true);

        try {
            let imageUrl = existingImageUrl;

            // Upload new image if provided
            if (communityImage) {
                // const webpFile = await convertToWebP(communityImage);
                const imageRef = storageRef(storage, `community_images/${id || Date.now()}.webp`);
                await uploadBytes(imageRef, communityImage);
                imageUrl = await getDownloadURL(imageRef);
            }

            if (isEditMode) {
                // ✅ Update existing community
                await update(ref(database, `Communities/${id}`), {
                    name: form.name,
                    description: form.description,
                    imageUrl,
                });
                window.showToast("Community updated successfully", "success");
                navigate(-1);
            } else {
                // ✅ Create new community
                const communityRef = push(ref(database, "Communities"));
                const communityId = communityRef.key;

                const communityData = {
                    id: communityId,
                    name: form.name,
                    description: form.description,
                    imageUrl,
                    hostId: currentUser.uid,
                    hostName: currentUser.name || currentUser.email,
                    createdAt: Date.now(),
                    memberCount: 0,
                };

                await set(communityRef, communityData);

                const userCommunityRef = ref(
                    database,
                    `Users/${currentUser.uid}/hostedCommunities/${communityId}`
                );
                await set(userCommunityRef, true);

                navigate(`${ROUTES.COMMUNITY_CHAT}/${communityId}`, { replace: true });
            }
        } catch (error) {
            console.error("Error saving community:", error);
            alert("Failed to save community");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title={isEditMode ? "Edit Community" : "New Community"} isBackBtn={true} />

            <div className="flex">
                <Sidebar />
                <main className="flex-1 h-screen pt-5 pb-20 px-6 overflow-y-auto bg-white dark:bg-[#1A1A1A]">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            {isEditMode ? "Edit Community" : "New Community"}
                        </h2>
                        <div />
                    </div>

                    <div className="max-w-xl mx-auto md:pt-10 pt-3 space-y-6">
                        {/* Image */}
                        <div className="flex flex-col items-center">
                            <div
                                className="w-32 h-32 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center cursor-pointer overflow-hidden"
                                onClick={() => fileInputRef.current.click()}
                            >
                                {communityImage ? (
                                    <img
                                        src={URL.createObjectURL(communityImage)}
                                        alt="Community"
                                        className="w-full h-full object-cover"
                                    />
                                ) : existingImageUrl ? (
                                    <img src={existingImageUrl} alt="Community" className="w-full h-full object-cover" />
                                ) : (
                                    <FiCamera className="text-3xl dark:text-gray-400 text-gray-700" />
                                )}
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                accept="image/*"
                                className="hidden"
                            />
                            <p className="text-sm dark:text-gray-400 text-gray-700 mt-2">
                                {isEditMode ? "Change community photo" : "Add community photo"}
                            </p>
                        </div>

                        {/* Text Fields */}
                        <input
                            type="text"
                            name="name"
                            placeholder="Community Name"
                            value={form.name}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-white text-black border border-gray-200"
                        />

                        <textarea
                            name="description"
                            placeholder="Community Description"
                            value={form.description}
                            onChange={handleChange}
                            className="min-h-[120px] w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-white text-black border border-gray-200"
                        />

                        <button
                            onClick={handleSave}
                            disabled={isSubmitting}
                            className="bg-primary text-white text-lg font-semibold py-3 rounded-xl w-full mt-4 disabled:opacity-50"
                        >
                            {isSubmitting
                                ? isEditMode
                                    ? "Updating..."
                                    : "Creating..."
                                : isEditMode
                                    ? "Save Changes"
                                    : "Create Community"}
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default NewCommunity;
